import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, GraduationCap, Users, LogOut, Tent } from "lucide-react";
import { Button } from "@/components/ui/button";

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";

  const staffTabs = [{ to: "/", label: "Training", Icon: GraduationCap }];
  const adminTabs = [
    { to: "/admin", label: "Dashboard", Icon: LayoutGrid },
    { to: "/admin/staff", label: "Staff", Icon: Users },
    { to: "/admin/modules", label: "Modules", Icon: GraduationCap },
  ];
  const tabs = isAdmin ? adminTabs : staffTabs;

  const active = (to) => (to === "/" || to === "/admin" ? location.pathname === to : location.pathname.startsWith(to));

  return (
    <div className="App min-h-screen bg-background pb-24 md:pb-0">
      <header className="sticky top-0 z-40 border-b border-border bg-background">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <button
            data-testid="brand-home-button"
            onClick={() => navigate(isAdmin ? "/admin" : "/")}
            className="flex items-center gap-2.5"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Tent className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <span className="text-left leading-tight">
              <span className="block font-heading text-sm font-extrabold tracking-tight text-foreground">Shady Acres</span>
              <span className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Staff Training</span>
            </span>
          </button>

          <nav className="hidden items-center gap-1 md:flex">
            {tabs.map(({ to, label, Icon }) => (
              <button
                key={to}
                data-testid={`nav-${label.toLowerCase()}`}
                onClick={() => navigate(to)}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                  active(to) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <span className="hidden text-right sm:block">
              <span className="block text-sm font-semibold text-foreground">{user?.name}</span>
              <span className="block text-[11px] capitalize text-muted-foreground">{user?.role}</span>
            </span>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="icon"
              className="rounded-full text-muted-foreground hover:text-destructive"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">{children}</main>

      {/* mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background md:hidden">
        <div className="mx-auto flex max-w-5xl">
          {tabs.map(({ to, label, Icon }) => (
            <button
              key={to}
              data-testid={`bottomnav-${label.toLowerCase()}`}
              onClick={() => navigate(to)}
              className={`flex flex-1 flex-col items-center gap-1 py-3 text-[11px] font-semibold transition-colors duration-200 ${
                active(to) ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <Icon className="h-5 w-5" strokeWidth={active(to) ? 2.4 : 2} />
              {label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
